// web/src/screens/admin/ref_inputs.ts
//
// ADMIN-07b — inline LV authoring widgets for entity reference slots:
//   - mountTextLVInput     — a single-Text slot rendered as one plain text
//                            input (Game.title_text, Task.correct_answer_text).
//                            Server auto-creates/updates the linked Text from
//                            the LV string on parent PATCH.
//   - mountTextSetLVInput  — a TextSet slot rendered as an ordered list of
//                            plain text inputs (Game.intro_phrase /
//                            default_prompt, Task.prompt / alt_accepted, all
//                            5 on_*_speak_textset). Server auto-creates/updates
//                            the linked named TextSet + member Texts.
//
// The widgets DO NOT load text/textset catalogues — they just hold the LV
// strings the author typed. The server handles short_code derivation,
// uniqueness, shared-edit detection (422 shared_edit_requires_confirm).
//
// Each widget surfaces the currently-linked short_code as a small grey
// click-through tag → /admin/texts/<sc> or /admin/textsets/<sc> where the
// "Used by" panel + audio-rebuild + name editing live.
//
// All API / caller text rendered via .textContent / input.value only — never
// raw-HTML setters (admin-screen XSS lock).

import { closeOpenTextPopup } from "./popup_stack.js";

// Re-export so existing imports of closeOpenTextPopup from this module keep
// working. The popup-stack helper is referenced by other admin screens.
export { closeOpenTextPopup };

// ── small inline-styled helpers ──────────────────────────────────────────

function makeLVInput(value = "", placeholder = "Latvian text…"): HTMLInputElement {
  const el = document.createElement("input");
  el.type = "text";
  el.value = value;
  el.placeholder = placeholder;
  el.lang = "lv";
  el.spellcheck = false;
  el.setAttribute(
    "style",
    "padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px; width: 100%; box-sizing: border-box;",
  );
  return el;
}

function smallBtn(text: string, title?: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.textContent = text;
  if (title) b.title = title;
  b.setAttribute(
    "style",
    "padding: 4px 8px; background: #e5e7eb; color: #111827; border: 0; border-radius: 4px; cursor: pointer; font-size: 12px; line-height: 1; min-width: 26px;",
  );
  return b;
}

function shortCodeTag(sc: string, navTo: string): HTMLAnchorElement {
  const a = document.createElement("a");
  a.textContent = sc;
  a.href = navTo;
  a.title = "Open in TextSets/Texts (shows Used-by and lets you rename)";
  a.setAttribute(
    "style",
    "font-size: 12px; color: #6b7280; font-family: ui-monospace, monospace; padding: 1px 6px; border: 1px solid #e5e7eb; border-radius: 4px; background: #f9fafb; text-decoration: none;",
  );
  a.addEventListener("click", (e) => {
    e.preventDefault();
    history.pushState({}, "", navTo);
    window.dispatchEvent(new PopStateEvent("popstate"));
  });
  return a;
}

// ── mountTextLVInput ─────────────────────────────────────────────────────

export interface TextLVInputHandle {
  /** Current LV string trimmed. */
  value(): string;
  /** Current short_code (null when no Text is linked yet). */
  shortCode(): string | null;
  /** Replace the displayed LV and (optionally) the short_code tag. */
  setValue(lv: string, sc?: string | null): void;
  unmount(): void;
}

export function mountTextLVInput(
  host: HTMLElement,
  opts: {
    initialLV?: string | null;
    initialShortCode?: string | null;
    placeholder?: string;
    onChange?: (lv: string) => void;
  },
): TextLVInputHandle {
  let currentSC: string | null =
    opts.initialShortCode && opts.initialShortCode !== "" ? opts.initialShortCode : null;
  let disposed = false;

  const row = document.createElement("div");
  row.setAttribute("style", "display: flex; flex-direction: column; gap: 4px; max-width: 520px;");

  const input = makeLVInput(opts.initialLV ?? "", opts.placeholder ?? "Latvian text…");
  input.addEventListener("input", () => {
    opts.onChange?.(input.value.trim());
  });
  row.appendChild(input);

  const meta = document.createElement("div");
  meta.setAttribute("style", "display: flex; align-items: center; gap: 8px; font-size: 12px; color: #6b7280;");
  row.appendChild(meta);

  function refresh(): void {
    if (disposed) return;
    meta.replaceChildren();
    if (currentSC) {
      meta.appendChild(shortCodeTag(currentSC, `/admin/texts/${encodeURIComponent(currentSC)}`));
    }
  }
  refresh();

  host.replaceChildren(row);

  return {
    value: () => input.value.trim(),
    shortCode: () => currentSC,
    setValue(lv: string, sc?: string | null): void {
      input.value = lv;
      if (sc !== undefined) currentSC = sc && sc !== "" ? sc : null;
      refresh();
    },
    unmount() {
      disposed = true;
      host.replaceChildren();
    },
  };
}

// ── mountTextSetLVInput ──────────────────────────────────────────────────
//
// Ordered list of plain text inputs (one per member). Add / Remove / Up /
// Down per row. The server folds blank rows out on save.

export interface TextSetLVInputHandle {
  /** Current list of LV strings (trim+drop-blank applied). */
  value(): string[];
  /** Current short_code (null when no TextSet is linked yet). */
  shortCode(): string | null;
  /** Replace the displayed list + (optional) short_code tag. */
  setValue(lvs: string[], sc?: string | null): void;
  unmount(): void;
}

export function mountTextSetLVInput(
  host: HTMLElement,
  opts: {
    initialLVs?: string[];
    initialShortCode?: string | null;
    placeholder?: string;
    onChange?: (lvs: string[]) => void;
    /**
     * When true, omit the short_code tag from the footer. Used by the
     * action-slot "non-shared / anonymous" speak picker, where the underlying
     * TextSet is anonymous and never gets a short_code.
     */
    hideShortCodeFooter?: boolean;
  },
): TextSetLVInputHandle {
  let currentSC: string | null =
    opts.initialShortCode && opts.initialShortCode !== "" ? opts.initialShortCode : null;
  let lvs: string[] = (opts.initialLVs ?? []).slice();
  let disposed = false;

  const root = document.createElement("div");
  root.setAttribute("style", "display: flex; flex-direction: column; gap: 6px; max-width: 520px;");

  const list = document.createElement("div");
  list.setAttribute("style", "display: flex; flex-direction: column; gap: 4px;");
  root.appendChild(list);

  const footer = document.createElement("div");
  footer.setAttribute("style", "display: flex; align-items: center; gap: 8px; font-size: 12px; color: #6b7280;");
  const addBtn = smallBtn("+ Add member", "Append an empty row");
  addBtn.addEventListener("click", () => {
    lvs.push("");
    render();
    notify();
    // Focus the freshly added input so the author can start typing immediately.
    const inputs = list.querySelectorAll<HTMLInputElement>("input[type=text]");
    inputs[inputs.length - 1]?.focus();
  });
  footer.appendChild(addBtn);
  root.appendChild(footer);

  function notify(): void {
    opts.onChange?.(lvs.filter((s) => s.trim() !== "").map((s) => s.trim()));
  }

  function render(): void {
    if (disposed) return;
    list.replaceChildren();
    if (lvs.length === 0) {
      const hint = document.createElement("p");
      hint.textContent = "(no members — click \"+ Add member\" to start)";
      hint.setAttribute("style", "margin: 0; font-size: 12px; color: #9ca3af; font-style: italic;");
      list.appendChild(hint);
    }
    for (let i = 0; i < lvs.length; i++) {
      const r = document.createElement("div");
      r.setAttribute("style", "display: flex; align-items: center; gap: 6px;");
      const numTag = document.createElement("span");
      numTag.textContent = String(i + 1);
      numTag.setAttribute(
        "style",
        "font-size: 11px; color: #6b7280; font-family: ui-monospace, monospace; min-width: 18px; text-align: right;",
      );
      r.appendChild(numTag);

      const input = makeLVInput(lvs[i] ?? "", opts.placeholder ?? "Latvian text…");
      input.addEventListener("input", () => {
        lvs[i] = input.value;
        notify();
      });
      r.appendChild(input);

      const upBtn = smallBtn("↑", "Move up");
      upBtn.disabled = i === 0;
      upBtn.style.opacity = i === 0 ? "0.4" : "1";
      upBtn.addEventListener("click", () => {
        if (i === 0) return;
        [lvs[i - 1], lvs[i]] = [lvs[i]!, lvs[i - 1]!];
        render();
        notify();
      });
      r.appendChild(upBtn);

      const downBtn = smallBtn("↓", "Move down");
      downBtn.disabled = i === lvs.length - 1;
      downBtn.style.opacity = i === lvs.length - 1 ? "0.4" : "1";
      downBtn.addEventListener("click", () => {
        if (i === lvs.length - 1) return;
        [lvs[i + 1], lvs[i]] = [lvs[i]!, lvs[i + 1]!];
        render();
        notify();
      });
      r.appendChild(downBtn);

      const rmBtn = smallBtn("✕", "Remove this member");
      rmBtn.addEventListener("click", () => {
        lvs.splice(i, 1);
        render();
        notify();
      });
      r.appendChild(rmBtn);

      list.appendChild(r);
    }
    // footer: short_code tag (if linked) + member count
    footer.replaceChildren();
    if (!opts.hideShortCodeFooter && currentSC) {
      footer.appendChild(shortCodeTag(currentSC, `/admin/textsets/${encodeURIComponent(currentSC)}`));
    }
    footer.appendChild(addBtn);
  }

  render();
  host.replaceChildren(root);

  return {
    value: () => lvs.filter((s) => s.trim() !== "").map((s) => s.trim()),
    shortCode: () => currentSC,
    setValue(next: string[], sc?: string | null): void {
      lvs = (next ?? []).slice();
      if (sc !== undefined) currentSC = sc && sc !== "" ? sc : null;
      render();
    },
    unmount() {
      disposed = true;
      host.replaceChildren();
    },
  };
}
